import { useApolloClient } from "@apollo/react-hooks";
import { useCallback, useState, useEffect } from "react";
import {
  GET_ALLERGY_GROUPS,
  GET_MOLECULES,
} from "./queries";
import { useToasts } from "react-toast-notifications";
import { isEmpty } from "lodash";

const INITIAL_STATE = {
  data: [],
  dataOptions: [],
  isAllergic: false,
  filterType: "GRUPO", // "GRUPO" | "MOLECULA"
  searchName: "",
  // Modal state
  isOpenModal: false,
  selectedItem: {},
  isLoadingSelect: false,
};

export default function useActions({
  dataAppointment,
  dataDoctor,
  data,
  onChangeFather,
  handleUpdateDeniesHistory,
}) {
  const [state, setState] = useState(INITIAL_STATE);
  const { addToast } = useToasts();
  const client = useApolloClient();

  const onChange = useCallback((name, value) => {
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const toggleModal = useCallback((isOpenModal = false, selectedItem = {}) => {
    setState((prev) => ({
      ...prev,
      selectedItem,
      isOpenModal,
    }));
  }, []);

  const handleRemoveData = useCallback(
    (original) => {
      const type = original.type === "GRUPO" ? "allergyGroup" : "molecule";

      const indexItem = data?.findIndex(
        (item) =>
          item[type]?.id === original[type]?.id && item.type === original.type,
      );      

      if (indexItem === -1) return;

      if (original?.id) {
        data[indexItem] = {
          ...data[indexItem],
          isRemoved: true,
          removedByMedicalStaffId: dataDoctor.medicalStaffId,
          removedBySpecialityId: dataAppointment?.specialityId,
          removedDate: new Date().toISOString().split("T")[0],
        };
      } else {
        data.splice(indexItem, 1);
      }

      onChangeFather("tabAllergies", [...data], "data");
    },
    [data, dataDoctor.medicalStaffId, dataAppointment?.specialityId, onChangeFather],
  );

  // Alergias guardadas que se borraron en pantalla pero siguen en la base hasta guardar.
  const hasUnsavedRemovals = data?.some((item) => item?.isRemoved && item?.id);

  const handleDeniesHistory = useCallback(() => {
    if (hasUnsavedRemovals) {
      addToast(
        "Debe guardar los cambios de alergias antes de indicar que el paciente niega historial.",
        { appearance: "warning", autoDismiss: true },
      );
      return;
    }
    handleUpdateDeniesHistory(true);
  }, [addToast, handleUpdateDeniesHistory, hasUnsavedRemovals]);

  const handleAddMoleculeOrGroup = useCallback(
    (original) => {
      const key = state.filterType === "GRUPO" ? "allergyGroup" : "molecule";

      const payload = {
        medicalStaffId: dataAppointment?.medicalStaffId,
        patientId: dataAppointment?.patientId,
        branchOfficeId: dataDoctor?.branchOfficeId,
        pharmacyProviderId: dataDoctor?.centerId,
        specialityId: dataAppointment?.specialityId,
        type: state?.filterType,
        appointmentId: dataAppointment?.id,
        [key]: original,
      };

      onChangeFather("tabAllergies", [...(data || []), payload], "data");
    },
    [
      data,
      dataAppointment.id,
      dataAppointment.medicalStaffId,
      dataAppointment.patientId,
      dataAppointment.specialityId,
      dataDoctor.branchOfficeId,
      dataDoctor.centerId,
      onChangeFather,
      state.filterType,
    ],
  );

  const loadOptionsMoleculesOrAllergies = useCallback(
    async (inputValue) => {
      try {
        if (inputValue.trim().length < 3) return [];
        onChange("isLoadingSelect", true);
        if (state.filterType === "GRUPO") {
          const { data: { getAllergyGroup } = {} } = await client.query({
            query: GET_ALLERGY_GROUPS,
            variables: {
              where: {
                name: inputValue,
              },
              relations: [],
            },
          });

          const result =
            getAllergyGroup?.filter(
              (item) => !data?.some((i) => i.allergyGroup?.id === item.id),
            ) || [];
          return result;
        }
        const { data: { molecules } = {} } = await client.query({
          query: GET_MOLECULES,
          variables: {
            where: {
              name: inputValue,
              isAllergic: false,
            },
          },
        });

        const result =
          molecules?.filter(
            (item) => !data?.some((i) => i.molecule?.id === item.id),
          ) || [];

        return result;
      } catch (error) {
        addToast(`Error cargando opciones: ${error.message}`, {
          appearance: "error",
          autoDismiss: true,
        });
        return [];
      } finally {
        onChange("isLoadingSelect", false);
      }
    },
    [addToast, client, data, onChange, state.filterType],
  );

  useEffect(() => {
    if (!isEmpty(data)) setState((prev) => ({ ...prev, data }));
  }, [data]);

  return [
    { state },
    {
      onChange,
      toggleModal,
      handleAddMoleculeOrGroup,
      handleRemoveData,
      handleDeniesHistory,
      loadOptionsMoleculesOrAllergies,
    },
  ];
}
